import React, { Fragment, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import logoutUser from "features/auth/actions/auth.logoutUser";
import { AppDispatch, RootState } from "services/redux/rootReducer";
import NavbarIcon from "./navbarIcon";
import {
    Container,
    LinkContainer,
    LogoContainer,
    StyledLink,
    StyledLogout,
    StyledSublink,
} from "./adminNavbar.styled";
import { MAIN_NAVBAR, MENU_ITEMS } from "../constants/navbar";
import { NavbarMenuItem } from "../types/navbar";
import Logo from "assets/customIcons/logo";

const AdminNavbar: React.FC = () => {
    const location = useLocation();

    const dispatch: AppDispatch = useDispatch();
    const userRole = useSelector((state: RootState) => state.auth.role);

    const handleLogout = () => {
        dispatch(logoutUser()).catch(() => alert("Logout failed"));
    };

    const generateDisplayedSublinks = useCallback(
        (parentMenuItemAccessor: string) => {
            const displayedSubMenuItems = MENU_ITEMS.filter(
                (menuItem) =>
                    menuItem.accessor.startsWith(parentMenuItemAccessor) &&
                    menuItem.accessor !== parentMenuItemAccessor
            );
            const displayedSublinks = displayedSubMenuItems.map(
                (subMenuItem) => {
                    if (
                        subMenuItem.rolesPermitted &&
                        !subMenuItem.rolesPermitted.includes(userRole)
                    )
                        return null;
                    return (
                        <StyledSublink
                            to={subMenuItem.route || "/"}
                            $active={subMenuItem.route === location.pathname}
                            key={subMenuItem.accessor}
                        >
                            <NavbarIcon
                                iconName={subMenuItem.displayIcon || ""}
                            />
                            <span>{subMenuItem.displayText}</span>
                        </StyledSublink>
                    );
                }
            );
            return displayedSublinks;
        },
        [location, userRole]
    );

    const displayedPrimaryLinks = useMemo(() => {
        return MAIN_NAVBAR.menuItems.map((menuItemAccessor) => {
            const menuItem = MENU_ITEMS.find(
                (menuItem) => menuItem.accessor === menuItemAccessor
            ) as NavbarMenuItem;

            // don't render anything if the user isn't meant to see that item
            if (
                menuItem.rolesPermitted &&
                !menuItem.rolesPermitted.includes(userRole)
            )
                return null;

            const isSelected =
                menuItem.route?.includes(
                    location.pathname.split("/").filter((str) => str)[0]
                ) || false;

            return (
                <Fragment key={menuItem.route}>
                    <StyledLink to={menuItem.route || "#"} $active={isSelected}>
                        <NavbarIcon iconName={menuItem.displayIcon || ""} />
                        <span>{menuItem.displayText}</span>
                    </StyledLink>
                    {isSelected && generateDisplayedSublinks(menuItem.accessor)}
                </Fragment>
            );
        });
    }, [location, userRole, generateDisplayedSublinks]);

    return (
        <Container>
            <LogoContainer>
                <div>
                    <Logo />
                </div>
                <hr />
            </LogoContainer>
            <LinkContainer>{displayedPrimaryLinks}</LinkContainer>
            <StyledLogout>
                <div onClick={() => handleLogout()}>
                    {MAIN_NAVBAR.logoutText}
                </div>
            </StyledLogout>
        </Container>
    );
};

export default AdminNavbar;
